import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  useTheme,
  Button,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Images } from "../../utils/Images";
import AuthenticationTextInput from "../../components/common/TextInput/AuthenticationTextInput";
import { DonateButtonVariant } from "../../components/common/Button/Button";
import DonateLeftBanner from "../../components/common/DonateLeftBanner";
import { styled } from "@mui/material/styles";
import BannerSection from "../../components/common/BannerSection";
import CurrencySelectorPopup from "../../components/common/CurrencySelectorPopup";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { handleDonate } from "../../utils/Donation";
import { useSelector, useDispatch } from "react-redux";
import { validateDonationAmount } from "../../utils/donationValidation";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";

const CustomDonateButton = styled(Button)(({ theme, isMobile, isTablet }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  // padding: isMobile ? "12px 16px" : isTablet ? "14px 20px" : "15px 24px",
  width: "100%", // 100% width for responsiveness
  height: isMobile ? "44px" : isTablet ? "48px" : "52px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
  lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : isTablet ? "7px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
}));

// Support options will be generated dynamically based on amount (0% always gives ₹0; default is 12%)
const getSupportOptions = (amount) => {
  const base = [
    { label: "0% - ₹0", value: "0" },
    { label: "12% - ₹0", value: "12" },
    { label: "15% - ₹0", value: "15" },
    { label: "20% - ₹0", value: "20" },
  ];
  if (!amount || Number(amount) <= 0) {
    return base;
  }
  const donationAmount = Number(amount);
  return [
    { label: "0% - ₹0", value: "0" },
    { label: `12% - ₹${Math.round(donationAmount * 0.12)}`, value: "12" },
    { label: `15% - ₹${Math.round(donationAmount * 0.15)}`, value: "15" },
    { label: `20% - ₹${Math.round(donationAmount * 0.20)}`, value: "20" },
  ];
};

const currencyList = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "د.إ" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "GBP", symbol: "£" },
];

const DonateOncePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const cmsHome = useSelector((state) => state.cms.home);

  // Fetch CMS data if not available
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);
  
  const handleDonateClick = async () => {
    // Validate form
    if (!form.name || !form.email || !form.postal) {
      alert("Please fill all required fields");
      return;
    }
    const phoneCheck = validateIndianMobile(form.phone);
    if (!phoneCheck.isValid) {
      alert(phoneCheck.error);
      return;
    }
    
    // Validate minimum donation amount (regular payment, not overseas)
    const validation = validateDonationAmount(amount, cmsHome, false, currency.symbol);
    if (!validation.isValid) {
      setAmountError(validation.error);
      return;
    }
    
    // Clear error if validation passes
    setAmountError("");

    // Google Tag Manager — begin checkout.
    //
    // Fired after validation passes, so a rejected form never reports a
    // checkout the user did not actually start. order_value is the amount the
    // user entered, excluding the tip.
    //
    // Deliberately not deduped: abandoning the Razorpay sheet and starting
    // again is a genuine second checkout, unlike the purchase event which must
    // fire once per order.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "gtm.beginCheckout",
      order_value: donationAmount,
      currency: currency.code,
    });

    try {
      const additionalData = {
        currency: currency.code,
        postalCode: form.postal,
        whatsappUpdates: whatsapp,
        campaignId: campaignId,
        campaignName: campaignTitle,
        tipAmount: tipAmount,
        anonymousCheck: anonymousCheck ? 1 : 0,
        // Pooled donate-once (from DonationBoxV2): backend routes the credit by beneficiary priority
        ...(location.state?.isPoolDonation === true && { isPoolDonation: true })
      };

      await handleDonate(
        form,
        Number(totalAmount), // Pass total amount (donation + tip)
        navigate,
        additionalData,
        user?.id,
        null // No snackbar for now
      );
    } catch (error) {
      console.error("Donation error:", error);
      alert("An error occurred. Please try again.");
    }
  };
  const theme = useTheme();
  // Custom media query for screens 900px to 1200px
  const isMediumScreen = useMediaQuery(
    "(min-width: 900px) and (max-width: 1200px)"
  );
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");

  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Get campaign title, amount, currency, and campaignId from navigation state
  const campaignTitleFromState = location.state?.campaignTitle || "";
  const amountFromState = location.state?.amount;
  const currencyFromState = location.state?.currency;
  const campaignIdFromState = location.state?.campaignId;

  // DonateRightForm state and handlers
  const [supportBy, setSupportBy] = useState("12");
  const [amount, setAmount] = useState(
    amountFromState !== undefined && amountFromState !== null
      ? String(amountFromState)
      : "3000"
  );
  const [currency, setCurrency] = useState(currencyFromState || currencyList[0]);
  const [campaignTitle, setCampaignTitle] = useState(campaignTitleFromState);
  const [campaignId, setCampaignId] = useState(campaignIdFromState);

  // Calculate tip and total amount (0% = no tip, default 12%)
  const donationAmount = Number(amount) || 0;
  const tipPercentage = supportBy === "0" ? 0 : (Number(supportBy) || 12);
  const tipAmount = Math.round(donationAmount * (tipPercentage / 100));
  const totalAmount = donationAmount + tipAmount;

  // Get dynamic support options based on amount
  const supportOptions = getSupportOptions(amount);

  // Fetch CMS data if not available
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  // Update amount and currency when location state changes
  useEffect(() => {
    if (amountFromState !== undefined && amountFromState !== null) {
      setAmount(String(amountFromState));
    }
    if (currencyFromState) {
      setCurrency(currencyFromState);
    }
    if (campaignTitleFromState) {
      setCampaignTitle(campaignTitleFromState);
    }
    if (campaignIdFromState) {
      setCampaignId(campaignIdFromState);
    }
  }, [amountFromState, currencyFromState, campaignTitleFromState, campaignIdFromState]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal: "",
  });
  const [whatsapp, setWhatsapp] = useState(true);
  const [anonymousCheck, setAnonymousCheck] = useState(false);
  const [amountError, setAmountError] = useState(""); // Validation error for amount

  // Auto-fill form when user is logged in
  useEffect(() => {
    if (user && user.id) {
      // Only auto-fill if fields are empty and user has the data
      setForm((prevForm) => ({
        ...prevForm,
        name: prevForm.name || user.name || "",
        email: prevForm.email || user.email || "",
        phone: prevForm.phone || user.phone || user.mobile || "",
        // postal code is not available in user profile, so leave it empty
      }));
    }
  }, [user]);

  const handleInputChange = (field) => (e) => {
    const value = sanitizeFormFieldValue(field, e.target.value);
    setForm({ ...form, [field]: value });
  };

  const handleAmountChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    setAmount(digitsOnly);
    if (digitsOnly && Number(digitsOnly) < 500) {
      setAmountError("Minimum donation amount is ₹500");
    } else {
      setAmountError("");
    }
  };

  // Currency dropdown handlers
  const handleCurrencyClick = (event) => {
    if (isMobile) {
      setShowCurrencyPopup(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCurrencySelect = (cur) => {
    setCurrency(cur);
    setAnchorEl(null);
    // If currency changes to non-INR, navigate to overseas-donor-page
    if (cur.code !== "INR") {
      navigate("/overseas-donor-page", {
        state: {
          campaignTitle: campaignTitle,
          campaignId: campaignId,
          amount: amount,
          currency: cur,
        },
      });
    }
  };

  const handleCurrencyPopupSelect = (cur) => {
    setCurrency(cur);
    // If currency changes to non-INR, navigate to overseas-donor-page
    if (cur.code !== "INR") {
      navigate("/overseas-donor-page", {
        state: {
          campaignTitle: campaignTitle,
          campaignId: campaignId,
          amount: amount,
          currency: cur,
        },
      });
    }
  };

  const handleCloseCurrencyPopup = () => {
    setShowCurrencyPopup(false);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: "40px",
          mt: "30px",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <BannerSection title="Donate Once" subtitle={false} />
      </Box>

      <Box
        sx={{
          background: isMobile ? "unset" : "rgba(232, 232, 232, 0.40)",
          p: isMobile ? "0" : isMediumScreen ? "16px" : "30px",
          width: "94.4%",
          maxWidth: "1440px",
          borderRadius: "20px",
          mx: "auto",
        }}
      >
        <Box sx={{ display: "flex", gap: isSmallScreen ? "0" : "24px" }}>
          {/* DonateRightForm content */}
          {/* DonateLeftBanner */}
          <Box>
            <DonateLeftBanner />
          </Box>
          <Box
            sx={{
              background: "#fff",
              borderRadius: isMobile ? "20px" : "20px",
              boxShadow: isMobile ? "none" : "0 4px 24px rgba(0,0,0,0.08)",
              p: isMobile ? "0px" : "30px 26px",
              pb: isSmallScreen ? "80px" : undefined, // breathing room for fixed donate bar on mobile
              minHeight: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
            }}
          >
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              <Typography
                sx={{
                  fontSize: isMobile
                    ? "12px"
                    : isMediumScreen
                    ? "14px"
                    : "18px",
                  color: "#1D1D1B",
                  fontWeight: 700,
                  display: isMobile ? "none" : "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: "16px",
                }}
              >
                <Box component="span">
                  <img
                    src={Images.streamlineSharpStar}
                    alt="Orange Sparks"
                    style={{
                      height: "auto",
                      zIndex: 1,
                    }}
                  />
                </Box>
                Donate Once
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile
                    ? "12px"
                    : isMediumScreen
                    ? "14px"
                    : "20px",
                  fontWeight: 700,
                  color: "#1D1D1B",
                  mb: isMobile ? 1 : "16px",
                  lineHeight: isMobile ? "20px" : 1.2,
                  letterSpacing: "-0.4px",
                }}
              >
                {campaignTitle || "This Janmashtami, Help Abandoned & Helpless Widows Have Healthy Meal To Survive"}
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile
                    ? "12px"
                    : isMediumScreen
                    ? "13px"
                    : "14px",
                  color: isMobile ? "#979796" : "#686866",
                  fontWeight: 400,
                  mb: isMobile ? "14px" : "16px",
                  lineHeight: 1.5,
                }}
              >
                By endorsing True Hope Foundation, your support enables us to
                consistently enhance our services for donors, ensuring they have
                an optimal experience on our platform. A portion of your tip
                will be allocated towards the promotion and expansion of the
                impact generated by this fundraising initiative.
              </Typography>
            </Box>
            {/* Support us by */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#E8E8E8",
                padding: "10px 10px 10px 20px",
                borderRadius: isMobile ? "4px" : "10px",
                mb: "22px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? "12px" : isSmallScreen ? "14px" : "16px",
                  color: "#141414",
                }}
              >
                Support us by :
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box>
                  <select
                    value={supportBy}
                    onChange={(e) => setSupportBy(e.target.value)}
                    style={{
                      minWidth: isMobile ? "110px" : "160px",
                      background: "#fff",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: isMobile ? "10px" : "15px",
                      border: "1.114px solid #B9B9B8",
                      height: isMobile ? "32px" : "40px",
                    }}
                  >
                    {supportOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>
            </Box>
            {/* Currency + Amount compact input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "#FFFFFF",
                borderRadius: "8px",
                border: "1.114px solid #B9B9B8",
                px: 2,
                py: 1,
                minWidth: isMobile ? "120px" : "180px",
                fontWeight: 600,
                fontSize: isMobile ? "15px" : "18px",
                gap: 1,
                mb: 2,
              }}
            >
              {/* Currency display (read-only - set from previous page) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 700,
                  fontSize: isMobile ? "12px" : "18px",
                  color: "#4A4A49",
                  cursor: "default",
                  pr: 1,
                  minWidth: "55px",
                  borderRight: "0.835px solid #4A4A49",
                }}
              >
                <Box component="span" sx={{ color: "#4A4A49" }}>
                  {currency.symbol}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    ml: 1,
                    color: "#4A4A49",
                    fontWeight: 700,
                    fontSize: isMobile ? "12px" : "17px",
                  }}
                >
                  {currency.code}
                </Typography>
              </Box>
              <Box sx={{ fontWeight: 600, color: "#222", pl: 1, flex: 1 }}>
                <input
                  type="text"
                  limit={10}
                  maxLength={10}
                  inputMode="numeric"
                  value={amount}
                  onChange={handleAmountChange}
                  aria-label="Donation amount"
                  placeholder="Enter amount"
                  style={{
                    border: "none",
                    background: "transparent",
                    fontWeight: 700,
                    fontSize: isMobile ? "12px" : isMobile ? "14px" : "17px",
                    outline: "none",
                    width: "100%",
                    color: "#222",
                    cursor: "text",
                  }}
                />
              </Box>
            </Box>
            {/* Validation error text */}
            {amountError && (
              <Typography
                sx={{
                  color: "#F04951",
                  fontSize: isMobile ? "11px" : "12px",
                  mt: -1,
                  mb: 2,
                  ml: 1,
                }}
              >
                {amountError}
              </Typography>
            )}
            {/* Total Amount Display */}
            {donationAmount > 0 && (
              <Box
                sx={{
                  border: "1.114px solid #BDD188",
                  background: "#F7F9EC",
                  borderRadius: "8px",
                  px: "18px",
                  py: "13px",
                  mb: "18px",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: "8px" }}>
                  <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#4A4A49", fontWeight: 500 }}>
                    Donation Amount
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#1D1D1B", fontWeight: 600 }}>
                    {currency.symbol}{donationAmount}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: "8px" }}>
                  <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#4A4A49", fontWeight: 500 }}>
                    Tip Amount ({tipPercentage}%)
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#1D1D1B", fontWeight: 600 }}>
                    {currency.symbol}{tipAmount}
                  </Typography>
                </Box>
                <Box sx={{ borderTop: "1px solid #E0E0E0", pt: "8px", display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#1D1D1B", fontWeight: 700 }}>
                    Total Amount
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#1D1D1B", fontWeight: 700 }}>
                    {currency.symbol}{totalAmount}
                  </Typography>
                </Box>
              </Box>
            )}
            {/* Form fields */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "auto" : "auto auto",
                columnGap: "27px",
              }}
            >
              <Box>
                <AuthenticationTextInput
                  label="Full Name"
                  value={form.name}
                  onChange={handleInputChange("name")}
                  placeholder="Enter Full Name"
                  required
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Email ID"
                  value={form.email}
                  onChange={handleInputChange("email")}
                  placeholder="Enter E mail Id"
                  required
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Phone Number"
                  value={form.phone}
                  onChange={handleInputChange("phone")}
                  placeholder="Enter Phone Number"
                  phoneInput
                  required
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Postal Code"
                  value={form.postal}
                  onChange={handleInputChange("postal")}
                  placeholder="Enter Postal Code"
                  required
                />
              </Box>
            </Box>
            {/* Anonymous Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={anonymousCheck}
                  onChange={(e) => setAnonymousCheck(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  Make my donation anonymous
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* WhatsApp Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  I want to receive transaction and donation updates on WhatsApp
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* Terms */}
            <Typography
              sx={{
                fontSize: isMobile ? "10px" : "14px",
                color: "#1D1D1B",
                mb: isMobile ? 2 : 3,
                fontWeight: 500,
              }}
            >
              By continuing, you are agreeing to{" "}
              <Box
                component={Link}
                to="/terms-conditions"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Terms of Use
              </Box>{" "}
              and{" "}
              <Box
                component={Link}
                to="/privacy-policy"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Privacy Policy
              </Box>
            </Typography>
            {/* Donate Button */}
            <div
              style={{
                width: "100%",
                display: isSmallScreen ? "none" : "block",
              }}
            >
              <CustomDonateButton onClick={handleDonateClick} isMobile={isMobile}>
                {`Donate ${currency.symbol}${totalAmount > 0 ? totalAmount : amount}`}
              </CustomDonateButton>
            </div>
          </Box>
        </Box>
      </Box>
      <div
        style={{
          width: "100%",
          position: "fixed",
          backgroundColor: "#fff",
          bottom: 0,
          left: 0,
          padding: "16px 10px",
          boxShadow: " 0 -4px 16px 0 rgba(0, 0, 0, 0.10)",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <CustomDonateButton onClick={handleDonateClick} isMobile={isMobile}>
          {`Donate ${currency.symbol}${totalAmount > 0 ? totalAmount : amount}`}
        </CustomDonateButton>
      </div>
      {/* Currency Selector Popup */}
      <CurrencySelectorPopup
        open={showCurrencyPopup}
        onClose={handleCloseCurrencyPopup}
        selectedCurrency={currency}
        onCurrencySelect={handleCurrencyPopupSelect}
      />
    </Box>
  );
};

export default DonateOncePage;
